import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDownIcon } from "lucide-react";
import type { ReactElement } from "react";

import { isCmmdEmbedMode } from "../../embed/cmmdEmbedMode";
import { createCmmdApiClient } from "../../environments/cmmdApi";
import { cn } from "../../lib/utils";
import {
  switchCurrentForgeWorkspaceId,
  useCurrentForgeWorkspaceState,
} from "../../workspace/currentForgeWorkspaceStore";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "../ui/menu";
import { WorkspaceAvatar } from "./WorkspaceAvatar";

const PICKER_ITEM_CLASS =
  "h-8 min-h-8 gap-2 px-2 text-sm font-medium [&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:items-center [&>span:last-child]:gap-2";

/**
 * Always `switchCurrentForgeWorkspaceId`, never `seedCurrentForgeWorkspaceId`
 * -- even the first time this account ever picks a workspace here
 * (CMMD-Center/forge#3572). The switcher is a separate actor from whatever
 * else is reading the store, most notably a mounted first-run hook still
 * showing its own "ask" panel; only an explicit switch bumps
 * `switchGeneration`, which is what resets that hook's stale local pick. A
 * seed here (no generation bump) would leave that panel showing a workspace
 * the shell has already moved away from.
 */
export function applyForgeWorkspaceSelection(chosenOrganizationId: number): void {
  // CMMD owns workspace switching inside the embed iframe.
  if (isCmmdEmbedMode()) return;
  switchCurrentForgeWorkspaceId(chosenOrganizationId);
}

function workspaceIconUrl(workspace: {
  readonly iconUrl?: string | null;
  readonly icon_url?: string | null;
  readonly icon?: string | null;
}): string | null {
  return workspace.iconUrl ?? workspace.icon_url ?? workspace.icon ?? null;
}

function workspaceLogoUrl(workspace: {
  readonly logoUrl?: string | null;
  readonly logo_url?: string | null;
  readonly logo?: string | null;
}): string | null {
  return workspace.logoUrl ?? workspace.logo_url ?? workspace.logo ?? null;
}

/**
 * The one shell-level "which CMMD workspace" control (CMMD-Center/forge#3572
 * decision 2), mounted once in `Sidebar.tsx` so it is visible on every route
 * the sidebar renders, including Settings. Renders nothing for an account with
 * at most one CMMD workspace -- there is nothing to switch between.
 */
export function ForgeWorkspaceSwitcher(): ReactElement | null {
  // CMMD already owns workspace switching when Forge is embedded (`?embed=1`).
  if (isCmmdEmbedMode()) return null;

  const { workspaceId: currentWorkspaceId } = useCurrentForgeWorkspaceState();
  const bootstrapQuery = useQuery({
    queryKey: ["forge-workspace-switcher", "bootstrap", currentWorkspaceId ?? "none"],
    queryFn: () =>
      createCmmdApiClient().getBootstrap(
        currentWorkspaceId !== null ? { organizationId: currentWorkspaceId } : undefined,
      ),
    staleTime: 30_000,
  });
  const workspaces = bootstrapQuery.data?.workspaces ?? [];
  if (workspaces.length < 2) return null;

  const current = workspaces.find((workspace) => workspace.id === currentWorkspaceId) ?? null;

  return (
    <Menu>
      <MenuTrigger
        render={
          <button
            type="button"
            data-testid="forge-workspace-switcher-trigger"
            aria-label={
              current
                ? `Switch CMMD workspace, currently ${current.name}`
                : "Choose a CMMD workspace"
            }
            className={cn(
              "flex h-7 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-xs font-medium text-sidebar-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            )}
          />
        }
      >
        <WorkspaceAvatar
          name={current?.name ?? null}
          iconUrl={current ? workspaceIconUrl(current) : null}
          logoUrl={current ? workspaceLogoUrl(current) : null}
          className="inline-flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border/70 bg-muted text-[9px] font-semibold text-muted-foreground"
        />
        <span className="max-w-24 truncate">{current?.name ?? "Choose workspace"}</span>
        <ChevronsUpDownIcon className="size-3 shrink-0 opacity-60" />
      </MenuTrigger>
      <MenuPopup align="start" className="max-h-80 w-56 overflow-y-auto">
        <MenuRadioGroup
          value={current ? String(current.id) : undefined}
          onValueChange={(value) => {
            const parsed = Number.parseInt(String(value), 10);
            if (!Number.isSafeInteger(parsed)) return;
            applyForgeWorkspaceSelection(parsed);
          }}
        >
          {workspaces.map((workspace) => (
            <MenuRadioItem
              key={workspace.id}
              value={String(workspace.id)}
              closeOnClick
              hideIndicator
              className={PICKER_ITEM_CLASS}
            >
              <WorkspaceAvatar
                name={workspace.name}
                iconUrl={workspaceIconUrl(workspace)}
                logoUrl={workspaceLogoUrl(workspace)}
                className="inline-flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-border/70 bg-muted text-[9px] font-semibold text-muted-foreground"
              />
              <span className="min-w-0 truncate text-sm">{workspace.name}</span>
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}
